import React, { useState, useEffect } from 'react';
import { X, Plus, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function EmployeeModal({ employee, onClose }) {
  const [formData, setFormData] = useState({
    nome: employee?.nome || '',
    cognome: employee?.cognome || '',
    numero_matricola: employee?.numero_matricola || '',
    codice_fiscale: employee?.codice_fiscale || '',
    data_nascita: employee?.data_nascita ? employee.data_nascita.split('T')[0] : '',
    luogo_nascita: employee?.luogo_nascita || '',
    sesso: employee?.sesso || '',
    data_assunzione: employee?.data_assunzione ? employee.data_assunzione.split('T')[0] : '',
    ruolo: employee?.ruolo || '',
    qualifica: employee?.qualifica || '',
    tipo_contratto: employee?.tipo_contratto || '',
    sede: employee?.sede || '',
    email: employee?.email || '',
    telefono: employee?.telefono || '',
    indirizzo: employee?.indirizzo || '',
    note: employee?.note || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sedi, setSedi] = useState([]);
  const [newSede, setNewSede] = useState('');
  const [showAddSede, setShowAddSede] = useState(false);
  const [addingSede, setAddingSede] = useState(false);
  
  useEffect(() => {
    fetchSedi();
  }, []);
  
  const fetchSedi = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sedi`);
      if (res.ok) {
        const data = await res.json();
        setSedi(data);
      }
    } catch (err) {
      console.error('Error fetching sedi:', err);
    }
  };
  
  const handleAddSede = async () => {
    if (!newSede.trim()) return;
    
    try {
      setAddingSede(true);
      const res = await fetch(`${API_URL}/api/sedi?nome=${encodeURIComponent(newSede.trim())}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const created = await res.json();
        setSedi([...sedi, created].sort((a, b) => a.nome.localeCompare(b.nome)));
        setFormData({ ...formData, sede: created.nome });
        setNewSede('');
        setShowAddSede(false);
      }
    } catch (err) {
      console.error('Error adding sede:', err);
    } finally {
      setAddingSede(false);
    }
  };
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome) {
      setError('Nome e cognome sono obbligatori');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const payload = {};
      
      for (const [key, value] of Object.entries(formData)) {
        if (value === '') {
          continue;
        }
        
        if (key === 'data_nascita' || key === 'data_assunzione') {
          payload[key] = new Date(value).toISOString();
        } else {
          payload[key] = value;
        }
      }
      
      const url = employee
        ? `${API_URL}/api/employees/${employee._id}`
        : `${API_URL}/api/employees`;
      
      const method = employee ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Errore nel salvataggio');
      }
      
      onClose(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="employee-modal">
      <div className="bg-card rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {employee ? 'Modifica Dipendente' : 'Nuovo Dipendente'}
          </h2>
          <button
            onClick={() => onClose(false)}
            className="btn btn-secondary"
            data-testid="close-modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label className="form-label">Nome *</label>
              <input
                type="text"
                name="nome"
                className="form-input"
                value={formData.nome}
                onChange={handleChange}
                required
                data-testid="input-nome"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Cognome *</label>
              <input
                type="text"
                name="cognome"
                className="form-input"
                value={formData.cognome}
                onChange={handleChange}
                required
                data-testid="input-cognome"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Numero Matricola</label>
              <input
                type="text"
                name="numero_matricola"
                className="form-input font-mono"
                value={formData.numero_matricola}
                onChange={handleChange}
                placeholder="Es: MAT001"
                data-testid="input-numero-matricola"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Codice Fiscale</label>
              <input
                type="text"
                name="codice_fiscale"
                className="form-input"
                value={formData.codice_fiscale}
                onChange={handleChange}
                data-testid="input-codice-fiscale"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Data di Nascita</label>
              <input
                type="date"
                name="data_nascita"
                className="form-input"
                value={formData.data_nascita}
                onChange={handleChange}
                data-testid="input-data-nascita"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Luogo di Nascita</label>
              <input
                type="text"
                name="luogo_nascita"
                className="form-input"
                value={formData.luogo_nascita}
                onChange={handleChange}
                data-testid="input-luogo-nascita"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Sesso</label>
              <select
                name="sesso"
                className="form-input"
                value={formData.sesso}
                onChange={handleChange}
                data-testid="input-sesso"
              >
                <option value="">Non specificato</option>
                <option value="M">Maschio</option>
                <option value="F">Femmina</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleChange}
                data-testid="input-email"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Telefono</label>
              <input
                type="tel"
                name="telefono"
                className="form-input"
                value={formData.telefono}
                onChange={handleChange}
                data-testid="input-telefono"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Data Assunzione</label>
              <input
                type="date"
                name="data_assunzione"
                className="form-input"
                value={formData.data_assunzione}
                onChange={handleChange}
                data-testid="input-data-assunzione"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Ruolo</label>
              <input
                type="text"
                name="ruolo"
                className="form-input"
                value={formData.ruolo}
                onChange={handleChange}
                placeholder="Es: Operaio, Impiegato, Tecnico"
                data-testid="input-ruolo"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Qualifica</label>
              <input
                type="text"
                name="qualifica"
                className="form-input"
                value={formData.qualifica}
                onChange={handleChange}
                data-testid="input-qualifica"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Tipo Contratto</label>
              <select
                name="tipo_contratto"
                className="form-input"
                value={formData.tipo_contratto}
                onChange={handleChange}
                data-testid="input-tipo-contratto"
              >
                <option value="">Seleziona...</option>
                <option value="Tempo Indeterminato">Tempo Indeterminato</option>
                <option value="Tempo Determinato">Tempo Determinato</option>
                <option value="Apprendistato">Apprendistato</option>
                <option value="Stage">Stage</option>
                <option value="Collaborazione">Collaborazione</option>
              </select>
            </div>
            
            {/* Sede Dropdown with Add New Option */}
            <div className="form-group">
              <label className="form-label flex items-center gap-2">
                <MapPin size={16} className="text-primary" />
                Sede di Lavoro
              </label>
              {!showAddSede ? (
                <div className="flex gap-2">
                  <select
                    name="sede"
                    className="form-input flex-1"
                    value={formData.sede}
                    onChange={handleChange}
                    data-testid="input-sede"
                  >
                    <option value="">Seleziona sede...</option>
                    {sedi.map((s) => (
                      <option key={s._id} value={s.nome}>
                        {s.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowAddSede(true)}
                    className="btn btn-secondary px-3"
                    title="Aggiungi nuova sede"
                    data-testid="add-sede-button"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="form-input flex-1"
                    value={newSede}
                    onChange={(e) => setNewSede(e.target.value)}
                    placeholder="Nome nuova sede..."
                    autoFocus
                    data-testid="new-sede-input"
                  />
                  <button
                    type="button"
                    onClick={handleAddSede}
                    className="btn btn-primary px-3"
                    disabled={addingSede || !newSede.trim()}
                    data-testid="confirm-add-sede"
                  >
                    {addingSede ? '...' : 'Aggiungi'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSede(false);
                      setNewSede('');
                    }}
                    className="btn btn-secondary px-3"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}
            </div>
            
            <div className="form-group md:col-span-2">
              <label className="form-label">Indirizzo</label>
              <input
                type="text"
                name="indirizzo"
                className="form-input"
                value={formData.indirizzo}
                onChange={handleChange}
                data-testid="input-indirizzo"
              />
            </div>
            
            <div className="form-group md:col-span-2">
              <label className="form-label">Note</label>
              <textarea
                name="note"
                className="form-input form-textarea"
                value={formData.note}
                onChange={handleChange}
                rows={3}
                data-testid="input-note"
              />
            </div>
          </div>
          
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="btn btn-secondary"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Salvataggio...' : employee ? 'Aggiorna' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmployeeModal;
